'use client';

/**
 * BehaviorEngine — the invisible behavioral layer of the Living Lab OS.
 * Renders nothing; runs entirely in the visitor's browser.
 *
 * Responsibilities (and the ONLY place these happen):
 *   - route → zone sync (setZone / recordZone / ZONE_ENTERED / ZONE_EXITED)
 *   - idle tracking (setIdle, USER_IDLE / USER_ACTIVE)
 *   - intent inference (setIntent, INTENT_CHANGED) via src/intelligence/intent
 *   - proactive hints (setHint; one at a time, each id once per session)
 *
 * Privacy: session-local only. No network calls, no fingerprinting, no
 * persistent profile — sessionStorage is used solely to keep already-shown
 * hint ids from repeating within the tab session.
 */

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { useWorld, zoneForPath } from '../lab/store';
import { emitLabEvent, onLabEvent } from '../lab/events';
import type { LabHint, ZoneId } from '../lab/types';
import { useLang } from '../i18n/client';
import { getDict, type Dict } from '../i18n/dictionary';
import { inferIntent, nextHint, type BehaviorSignals } from './intent';

const IDLE_TICK_MS = 5_000;
const IDLE_THRESHOLD_MS = 30_000;
const INTENT_TICK_MS = 8_000;
const EVAL_DEBOUNCE_MS = 300;

/** sessionStorage key: hint ids already shown or dismissed this session. */
const HINTS_FIRED_KEY = 'slp-hints-fired';

function readFiredHints(): Set<string> {
  try {
    const raw = window.sessionStorage.getItem(HINTS_FIRED_KEY);
    if (!raw) return new Set();
    const arr: unknown = JSON.parse(raw);
    return new Set(
      Array.isArray(arr) ? arr.filter((x): x is string => typeof x === 'string') : [],
    );
  } catch {
    return new Set();
  }
}

function persistFiredHints(ids: Set<string>): void {
  try {
    window.sessionStorage.setItem(HINTS_FIRED_KEY, JSON.stringify([...ids]));
  } catch {
    // storage unavailable — hints simply won't dedupe across reloads
  }
}

/**
 * Re-source a hint's text + action label for a non-EN locale. The selection
 * rules, ids and (logical) hrefs are decided by `nextHint` and stay untouched;
 * only the presentation copy is swapped, keyed by hint id. The two
 * 'knowledge-path' variants are told apart by their target href.
 *
 * EN hints already carry their English copy from `nextHint`, so callers pass
 * them through unchanged — English output stays byte-identical.
 */
function localizeHint(hint: LabHint, dict: Dict): LabHint {
  const h = dict.hint;
  switch (hint.id) {
    case 'knowledge-path':
      return hint.href === '/research/knowledge-graphs'
        ? { ...hint, text: h.knowledgeResearch.text, actionLabel: h.knowledgeResearch.action }
        : { ...hint, text: h.knowledge.text, actionLabel: h.knowledge.action };
    case 'applied':
      return { ...hint, text: h.applied.text, actionLabel: h.applied.action };
    case 'publications':
      return { ...hint, text: h.publications.text, actionLabel: h.publications.action };
    default:
      return hint;
  }
}

export default function BehaviorEngine() {
  const pathname = usePathname();

  // Hints fire from long-lived timers/bus closures set up once at mount, so the
  // active locale is kept in a ref the closures can read fresh (see evaluate()).
  const lang = useLang();
  const langRef = useRef(lang);
  langRef.current = lang;

  const mountedAt = useRef(0);
  const prevZone = useRef<ZoneId | null>(null);
  const dwell = useRef<Partial<Record<ZoneId, number>>>({});
  const dwellStamp = useRef(0);
  const nodeFocusCount = useRef(0);
  const agentSelections = useRef(0);
  const lastActivity = useRef(0);
  const idleCrossed = useRef(false);
  const firedHints = useRef<Set<string>>(new Set());
  const lastHintAt = useRef<number | null>(null);
  const lastIntentSig = useRef('');
  const evalDebounce = useRef<number | null>(null);

  /** Accrue dwell time for the zone the visitor is currently in. */
  const accrueDwell = (now: number) => {
    const zone = useWorld.getState().zone;
    if (dwellStamp.current > 0) {
      dwell.current[zone] = (dwell.current[zone] ?? 0) + (now - dwellStamp.current);
    }
    dwellStamp.current = now;
  };

  const currentSignals = (now: number): BehaviorSignals => ({
    zoneDwellMs: dwell.current,
    nodeFocusCount: nodeFocusCount.current,
    agentSelections: agentSelections.current,
    sessionMs: now - mountedAt.current,
  });

  /** One evaluation pass: intent inference, then the hint check. */
  const evaluate = () => {
    const now = Date.now();
    accrueDwell(now);

    const world = useWorld.getState();
    const signals = currentSignals(now);
    const result = inferIntent(world.visitor, signals);

    const sig =
      result.intent +
      '|' +
      Object.entries(result.confidence)
        .map(([k, v]) => `${k}:${(v ?? 0).toFixed(2)}`)
        .join(',');
    if (sig !== lastIntentSig.current) {
      lastIntentSig.current = sig;
      const changed = result.intent !== world.visitor.intent;
      world.setIntent(result.intent, result.confidence);
      if (changed) emitLabEvent({ type: 'INTENT_CHANGED', intent: result.intent });
    }

    // Proactive hint — quiet: one at a time, each id once per session.
    const fresh = useWorld.getState();
    const hint = nextHint({
      visitor: fresh.visitor,
      signals,
      pathname: window.location.pathname,
      msSinceLastHint: lastHintAt.current === null ? null : now - lastHintAt.current,
      firedIds: firedHints.current,
      hintOpen: fresh.hint !== null,
    });
    if (hint && fresh.hint === null) {
      firedHints.current.add(hint.id);
      persistFiredHints(firedHints.current);
      lastHintAt.current = now;
      // EN hints are already English — pass through so EN stays identical;
      // other locales are re-sourced from the chrome dictionary.
      fresh.setHint(
        langRef.current === 'en'
          ? hint
          : localizeHint(hint, getDict(langRef.current)),
      );
    }
  };

  /** Coalesce bursts of bus events into one evaluation. */
  const scheduleEvaluate = () => {
    if (evalDebounce.current !== null) return;
    evalDebounce.current = window.setTimeout(() => {
      evalDebounce.current = null;
      evaluate();
    }, EVAL_DEBOUNCE_MS);
  };

  // ── Mount: idle tracking, intent loop, bus subscription ──────────────
  useEffect(() => {
    const now = Date.now();
    mountedAt.current = now;
    dwellStamp.current = now;
    lastActivity.current = now;
    firedHints.current = readFiredHints();

    const onActivity = () => {
      if (idleCrossed.current) {
        idleCrossed.current = false;
        useWorld.getState().setIdle(0);
        emitLabEvent({ type: 'USER_ACTIVE' });
      }
      lastActivity.current = Date.now();
    };
    const opts: AddEventListenerOptions = { passive: true };
    window.addEventListener('pointermove', onActivity, opts);
    window.addEventListener('keydown', onActivity, opts);
    window.addEventListener('scroll', onActivity, opts);
    window.addEventListener('touchstart', onActivity, opts);

    const idleTimer = window.setInterval(() => {
      const elapsed = Date.now() - lastActivity.current;
      useWorld.getState().setIdle(elapsed);
      if (elapsed >= IDLE_THRESHOLD_MS && !idleCrossed.current) {
        idleCrossed.current = true;
        emitLabEvent({ type: 'USER_IDLE', ms: elapsed });
      }
    }, IDLE_TICK_MS);

    const intentTimer = window.setInterval(evaluate, INTENT_TICK_MS);

    const offBus = onLabEvent((e) => {
      switch (e.type) {
        case 'NODE_FOCUSED':
          nodeFocusCount.current += 1;
          scheduleEvaluate();
          break;
        case 'AGENT_SELECTED':
          // Counted locally (not via recordAgentInteraction) so a page that
          // both records and emits is not double-counted; inference takes
          // max(visitor.agentInteractions, agentSelections).
          agentSelections.current += 1;
          scheduleEvaluate();
          break;
        case 'PROJECT_OPENED':
          useWorld.getState().recordProject(e.slug); // idempotent
          scheduleEvaluate();
          break;
        case 'RESEARCH_OPENED':
          useWorld.getState().recordResearch(e.slug); // idempotent
          scheduleEvaluate();
          break;
        default:
          break;
      }
    });

    return () => {
      window.removeEventListener('pointermove', onActivity);
      window.removeEventListener('keydown', onActivity);
      window.removeEventListener('scroll', onActivity);
      window.removeEventListener('touchstart', onActivity);
      window.clearInterval(idleTimer);
      window.clearInterval(intentTimer);
      if (evalDebounce.current !== null) window.clearTimeout(evalDebounce.current);
      offBus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Route → zone sync (the single place this happens) ────────────────
  useEffect(() => {
    if (!pathname) return;
    const zone = zoneForPath(pathname);
    const prev = prevZone.current;
    if (prev === zone) return;

    accrueDwell(Date.now());
    if (prev !== null) emitLabEvent({ type: 'ZONE_EXITED', zone: prev });
    prevZone.current = zone;

    const world = useWorld.getState();
    world.setZone(zone);
    world.recordZone(zone);
    emitLabEvent({ type: 'ZONE_ENTERED', zone });
    scheduleEvaluate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return null;
}
